import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Filter, Target, DollarSign, Trophy, Clock, Sparkles } from 'lucide-react';

export const FiltersBar = ({ filters, onFilterChange, onReset }) => {
  return (
    <div 
      className="p-4 md:p-5 bg-card border border-border rounded-xl shadow-apple"
      data-testid="filters-bar"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-foreground" />
          <span className="font-semibold text-foreground">Filters</span>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onReset}
          className="text-xs text-foreground hover:text-foreground"
          data-testid="filters-reset-button"
        >
          Reset
        </Button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {/* Ranking Mode */}
        <div data-testid="filter-ranking-mode">
          <Label className="text-xs font-medium text-foreground mb-1.5 block flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Rank By
          </Label>
          <Select 
            value={filters.rankingMode || 'roi'} 
            onValueChange={(v) => onFilterChange({ rankingMode: v })}
          >
            <SelectTrigger className="h-9 text-foreground">
              <SelectValue placeholder="ROI" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="roi">Best ROI (EV%)</SelectItem>
              <SelectItem value="absolute">Highest $ EV</SelectItem>
              <SelectItem value="upper_tier">Top 3 Tier Prizes</SelectItem>
              <SelectItem value="jackpot">Has Jackpot</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Price Tier */}
        <div data-testid="filter-price">
          <Label className="text-xs font-medium text-foreground mb-1.5 block flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Price
          </Label>
          <Select 
            value={filters.priceTier || 'All'} 
            onValueChange={(v) => onFilterChange({ priceTier: v })}
          >
            <SelectTrigger className="h-9 text-foreground">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Prices</SelectItem>
              <SelectItem value="$1">$1</SelectItem>
              <SelectItem value="$2">$2</SelectItem>
              <SelectItem value="$3">$3</SelectItem>
              <SelectItem value="$5">$5</SelectItem>
              <SelectItem value="$10">$10</SelectItem>
              <SelectItem value="$20+">$20+</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* NEW: Target Prize Amount */}
        <div data-testid="filter-target-prize">
          <Label className="text-xs font-medium text-foreground mb-1.5 block flex items-center gap-1">
            <Target className="w-3 h-3" /> Target Prize
          </Label>
          <Select 
            value={filters.targetPrize?.toString() || 'any'} 
            onValueChange={(v) => onFilterChange({ targetPrize: v === 'any' ? null : parseInt(v) })}
          >
            <SelectTrigger className="h-9 text-foreground">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Prize</SelectItem>
              <SelectItem value="100">$100+ Available</SelectItem>
              <SelectItem value="500">$500+ Available</SelectItem>
              <SelectItem value="1000">$1,000+ Available</SelectItem>
              <SelectItem value="5000">$5,000+ Available</SelectItem>
              <SelectItem value="10000">$10,000+ Available</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Min EV% */}
        <div data-testid="filter-min-ev">
          <Label className="text-xs font-medium text-foreground mb-1.5 block">
            Min ROI: {filters.minEvPercent || 0}%
          </Label>
          <Slider 
            min={0} 
            max={70} 
            step={5}
            value={[filters.minEvPercent || 0]}
            onValueChange={([v]) => onFilterChange({ minEvPercent: v })}
            className="mt-3"
          />
        </div>
        
        {/* Max Age */}
        <div data-testid="filter-age">
          <Label className="text-xs font-medium text-foreground mb-1.5 block flex items-center gap-1">
            <Clock className="w-3 h-3" /> Max Age: {filters.age || 365}d
          </Label>
          <Slider 
            min={30} 
            max={365} 
            step={30}
            value={[filters.age || 365]}
            onValueChange={([v]) => onFilterChange({ age: v })}
            className="mt-3"
          />
        </div>
        
        {/* Checkboxes */}
        <div className="flex flex-col gap-2 justify-center">
          <div className="flex items-center gap-2" data-testid="filter-jackpot">
            <Checkbox 
              id="has-jackpot"
              checked={filters.hasJackpot || false}
              onCheckedChange={(checked) => onFilterChange({ hasJackpot: checked })}
            />
            <Label htmlFor="has-jackpot" className="text-xs font-medium text-foreground cursor-pointer flex items-center gap-1">
              <Trophy className="w-3 h-3 text-warning" /> Jackpot Only
            </Label>
          </div>
          <div className="flex items-center gap-2" data-testid="filter-new-only">
            <Checkbox 
              id="new-only"
              checked={filters.newOnly || false}
              onCheckedChange={(checked) => onFilterChange({ newOnly: checked })}
            />
            <Label htmlFor="new-only" className="text-xs font-medium text-foreground cursor-pointer">
              New Games Only
            </Label>
          </div>
        </div>
      </div>
      
      {/* Active Filters Summary */}
      {(filters.priceTier !== 'All' || filters.hasJackpot || filters.targetPrize || filters.minEvPercent > 0) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
          {filters.priceTier !== 'All' && (
            <Badge variant="secondary" className="text-xs">{filters.priceTier} tickets</Badge>
          )}
          {filters.hasJackpot && (
            <Badge variant="secondary" className="text-xs">🏆 Has Jackpot</Badge>
          )}
          {filters.targetPrize && (
            <Badge variant="secondary" className="text-xs">${filters.targetPrize.toLocaleString()}+ prizes</Badge>
          )}
          {filters.minEvPercent > 0 && (
            <Badge variant="secondary" className="text-xs">{filters.minEvPercent}%+ ROI</Badge>
          )}
        </div>
      )}
    </div>
  );
};
